import React from 'react';
import { Alert, Button, Col, Row, Space, Table, Tag, Typography } from 'antd';
import { CheckCircleOutlined, EditOutlined, InfoCircleOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { TNumberingRange } from './types';

const { Title, Text } = Typography;

interface NumberingRangesTabProps {
  ranges: TNumberingRange[];
  loading: boolean;
  syncing: boolean;
  isSharedPool: boolean;
  onClickSync: () => void;
  onOpenCreateRange: () => void;
  onOpenEditRange: (record: TNumberingRange) => void;
  onSetDefault: (record: TNumberingRange) => void;
}

export const NumberingRangesTab: React.FC<NumberingRangesTabProps> = ({
  ranges,
  loading,
  syncing,
  isSharedPool,
  onClickSync,
  onOpenCreateRange,
  onOpenEditRange,
  onSetDefault
}) => {
  const columns = [
    {
      title: 'Prefijo',
      dataIndex: 'prefix',
      key: 'prefix',
      width: 90,
      render: (val: string) => <Tag color="blue" style={{ fontWeight: 600 }}>{val}</Tag>
    },
    {
      title: 'Factus ID',
      dataIndex: 'factus_range_id',
      key: 'factus_range_id',
      width: 100,
      render: (val?: number) =>
        val ? (
          <Tag color="geekblue">#{val}</Tag>
        ) : (
          <Tag color="orange">Local</Tag>
        )
    },
    {
      title: 'Resolución DIAN',
      key: 'resolution',
      render: (_: any, record: TNumberingRange) => (
        <div>
          <div><strong>{record.resolution_number || 'Sin resolución'}</strong></div>
          {record.resolution_date && <Text type="secondary" style={{ fontSize: 11 }}>Fecha: {record.resolution_date}</Text>}
        </div>
      )
    },
    {
      title: 'Rango Autorizado',
      key: 'range',
      render: (_: any, record: TNumberingRange) => (
        <span>{record.from_number} - {record.to_number}</span>
      )
    },
    {
      title: 'Consecutivo Actual',
      dataIndex: 'current_number',
      key: 'current_number',
      width: 120,
      render: (val: number) => <strong>{val || 0}</strong>
    },
    {
      title: 'Vigencia',
      key: 'validity',
      render: (_: any, record: TNumberingRange) => (
        <span style={{ fontSize: 12 }}>
          {record.valid_from ? `${record.valid_from} al ${record.valid_to || 'Indefinido'}` : 'Sin vigencia'}
        </span>
      )
    },
    {
      title: 'Predeterminado',
      key: 'is_default',
      width: 130,
      render: (_: any, record: TNumberingRange) => (
        record.is_default ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>Predeterminado</Tag>
        ) : (
          <Button size="small" type="link" onClick={() => onSetDefault(record)}>
            Hacer Predeterminado
          </Button>
        )
      )
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (val: boolean) => (
        <Tag color={val ? 'green' : 'default'}>{val ? 'Activo' : 'Inactivo'}</Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 80,
      render: (_: any, record: TNumberingRange) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => onOpenEditRange(record)} />
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>Resoluciones y Rangos de Numeración</Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<SyncOutlined spin={syncing} />}
              onClick={onClickSync}
              loading={syncing}
            >
              {isSharedPool ? 'Sincronizar por Resolución' : 'Sincronizar desde Factus'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onOpenCreateRange}
            >
              Nuevo Rango
            </Button>
          </Space>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message={
          isSharedPool
            ? "Modalidad Bolsa Compartida: Los rangos diferencian a esta empresa ante Factus y la DIAN. La sincronización requiere obligatoriamente prefijo o resolución para no importar rangos de otras empresas."
            : "Modalidad Bolsa Propia: Sincroniza automáticamente todos los rangos de la cuenta exclusiva de esta empresa en Factus o regístralos manualmente."
        }
        style={{ marginBottom: 12 }}
      />

      <Table
        dataSource={ranges}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: 'No hay rangos de numeración registrados para esta empresa' }}
      />
    </div>
  );
};
